/**
 * `/email-templates/:kind/edit` — edit the per-studio HTML override for one
 * transactional email template.
 *
 * Seeds the editor with the current stored override (via `read_email_template`)
 * or, when none exists, a tokenised default from the code renderer (via
 * `GET /api/email-templates/:kind/seed`). Save dispatches `edit_email_template`;
 * "Reset to default" clears the override (`reset:true`) so the code template
 * takes over again. "Preview" fills the `{{ tokens }}` with the seed's
 * `sample` — every placeholder a send of THIS email can fill, with a sample
 * value — and renders the result in the same sandboxed iframe as View. A
 * placeholder the sample does not have is one a send cannot fill either, and
 * the preview says so: a saved edit that uses one is dropped at send time.
 *
 * A marketing email (`marketing` in the seed, 2026-10-23) must keep its
 * unsubscribe link, and a marketing step's edit its plain-text version, or
 * `edit_email_template` refuses the Save: the page says so up front, and its
 * Preview runs the same check on the draft (`draftSaveProblems`).
 *
 * The editor opens only when BOTH reads answered (`editorLoad`). A saved edit
 * that could not be read is not "no saved edit": opening on the starting HTML
 * there would let a Save replace a version nobody was shown, so a failed load
 * shows the reason and "Try again" instead of the boxes.
 *
 * `EmailTemplateEditView` is everything the page shows as a function of its
 * state, with no effect in it, so the tests can render each state.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FLOW_OPT_OUT_LINK_EXAMPLE } from "@layer/canonical-data/email-unsubscribe";
import { api, ApiError } from "../lib/api";
import { dispatchAction } from "../lib/dispatch";
import {
  draftPreviewSubtitle,
  draftSaveProblems,
  editorLoad,
  editorLoadFailureText,
  fillSampleTokens,
  type EditorSeed,
} from "../lib/email-inventory";
import { EmailPreviewModal } from "../components/EmailPreviewModal";

interface SeedResponse extends EditorSeed {
  readonly kind: string;
}

interface ReadOverrideResponse {
  readonly kind: string;
  readonly override: { readonly html_body: string; readonly text_body: string | null } | null;
}

export interface EmailTemplateEditViewProps {
  readonly kind: string;
  readonly loading: boolean;
  /** Why the editor is closed; `null` once both reads answered. */
  readonly loadError: string | null;
  readonly hasOverride: boolean;
  /** A marketing email: a saved edit must keep its unsubscribe link. */
  readonly marketing: boolean;
  /** A marketing step: a saved edit must have a plain-text version. */
  readonly textRequired: boolean;
  readonly html: string;
  readonly text: string;
  readonly saving: boolean;
  /** A failed Save or Reset on an open editor. */
  readonly error: string | null;
  readonly status: string | null;
  readonly onHtmlChange: (value: string) => void;
  readonly onTextChange: (value: string) => void;
  readonly onSave: () => void;
  readonly onReset: () => void;
  readonly onPreview: () => void;
  readonly onRetry: () => void;
  readonly onBack: () => void;
  readonly children?: ReactNode;
}

export function EmailTemplateEditView(
  props: EmailTemplateEditViewProps,
): JSX.Element {
  const { kind, loading, loadError, hasOverride, marketing, textRequired, html, text, saving, error, status } =
    props;
  // The boxes, and any sentence about a saved override, only once both reads
  // answered: before that the page knows neither.
  const open = !loading && loadError === null;

  return (
    <div className="page-email-template-edit">
      <h1 className="page-title">
        Edit email · <code>{kind}</code>
      </h1>
      {open ? (
        <p className="dc-muted" style={{ maxWidth: "680px" }}>
          {hasOverride
            ? "This template has a saved override. Edit the HTML below, or reset to the built-in default."
            : "No override yet — you're editing a copy of the built-in default. Saving creates a per-studio override."}{" "}
          Use tokens like <code>{"{{ member.first_name }}"}</code> and{" "}
          <code>{"{{ trigger.trainer_display_name }}"}</code>; they're filled in per member at send time.{" "}
          Where a sentence ends on the name, use <code>{"{{ member.comma_first_name }}"}</code>{" "}
          instead: it adds the comma and the first name, or nothing for a member with no name, so a
          heading never reads &ldquo;&hellip;, there&rdquo;.{" "}
          Save refuses an edit that prints the member&apos;s name as it is stored (a full name, or a
          phone number), or ends a sentence on <code>{", {{ member.first_name }}"}</code>.
          {marketing ? (
            <>
              {" "}
              This is a marketing email, so it must keep its unsubscribe link,{" "}
              <code>{FLOW_OPT_OUT_LINK_EXAMPLE}</code>: Save refuses an edit without it.
            </>
          ) : null}
        </p>
      ) : null}

      {loading ? <p className="dc-muted">Loading…</p> : null}
      {!loading && loadError !== null ? (
        <>
          <p className="action-modal-error">{loadError}</p>
          <div className="modal-actions" style={{ gap: "10px", display: "flex" }}>
            <button type="button" className="row-action-btn" onClick={props.onRetry}>
              Try again
            </button>
            <button type="button" className="row-edit-modal-cancel" onClick={props.onBack}>
              Back
            </button>
          </div>
        </>
      ) : null}
      {error !== null ? <p className="action-modal-error">{error}</p> : null}
      {status !== null ? <p className="dc-muted">{status}</p> : null}

      {open ? (
        <>
          <div className="form-row">
            <label>HTML body</label>
            <textarea
              value={html}
              rows={20}
              spellCheck={false}
              style={{ fontFamily: "ui-monospace, monospace", fontSize: "12.5px" }}
              onChange={(e) => props.onHtmlChange(e.target.value)}
            />
          </div>
          <div className="form-row">
            <label>
              {textRequired
                ? "Plain-text version (required) — keep {{ flow.opt_out_url }} in it: it is the member's unsubscribe link"
                : marketing
                  ? "Plain-text version (optional) — sent with the HTML when written; keep {{ flow.opt_out_url }} in it"
                  : "Plain-text version (optional) — sent with the HTML when written"}
            </label>
            <textarea
              value={text}
              rows={6}
              spellCheck={false}
              style={{ fontFamily: "ui-monospace, monospace", fontSize: "12.5px" }}
              onChange={(e) => props.onTextChange(e.target.value)}
            />
          </div>
          <div className="modal-actions" style={{ gap: "10px", display: "flex" }}>
            <button type="button" className="row-action-btn" disabled={saving} onClick={props.onSave}>
              {saving ? "Saving…" : "Save"}
            </button>
            <button type="button" className="row-action-btn" onClick={props.onPreview}>
              Preview
            </button>
            {hasOverride ? (
              <button type="button" className="row-action-btn" disabled={saving} onClick={props.onReset}>
                Reset to default
              </button>
            ) : null}
            <button type="button" className="row-edit-modal-cancel" onClick={props.onBack}>
              Back
            </button>
          </div>
        </>
      ) : null}

      {props.children}
    </div>
  );
}

export function EmailTemplateEdit(): JSX.Element {
  const { kind = "" } = useParams<{ kind: string }>();
  const navigate = useNavigate();
  const [html, setHtml] = useState<string>("");
  const [text, setText] = useState<string>("");
  const [sample, setSample] = useState<Readonly<Record<string, string>>>({});
  // What a saved edit of this email must keep (the seed says; 2026-10-23).
  const [rules, setRules] = useState<{ readonly marketing: boolean; readonly text_required: boolean }>({
    marketing: false,
    text_required: false,
  });
  const [hasOverride, setHasOverride] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    void (async () => {
      try {
        const [seed, read] = await Promise.all([
          api.get<SeedResponse>(`/api/email-templates/${kind}/seed`),
          dispatchAction<{ kind: string }, ReadOverrideResponse>(
            "read_email_template",
            { kind },
          ),
        ]);
        if (cancelled) return;
        const load = editorLoad(seed, read);
        if (!load.ok) {
          setLoadError(load.message);
          return;
        }
        setHasOverride(load.hasSavedEdit);
        setHtml(load.html);
        setText(load.text);
        setSample(seed.sample);
        setRules({ marketing: seed.marketing, text_required: seed.text_required });
        setError(null);
      } catch (e) {
        if (!cancelled) {
          setLoadError(
            e instanceof ApiError ? editorLoadFailureText(e) : (e as Error).message,
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [kind, loadAttempt]);

  const onSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setStatus(null);
    try {
      const r = await dispatchAction<
        { kind: string; html_body: string; text_body?: string },
        { written: boolean; cleared: boolean }
      >("edit_email_template", {
        kind,
        html_body: html,
        ...(text.length > 0 ? { text_body: text } : {}),
      });
      if (!r.ok) {
        setError(`save_failed (${r.code ?? "unknown"}): ${r.message ?? ""}`);
      } else {
        setHasOverride(true);
        setStatus("Saved. Live on the next send.");
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }, [kind, html, text]);

  const onReset = useCallback(async () => {
    setSaving(true);
    setError(null);
    setStatus(null);
    try {
      const r = await dispatchAction<
        { kind: string; reset: boolean },
        { cleared: boolean }
      >("edit_email_template", { kind, reset: true });
      if (!r.ok) {
        setError(`reset_failed (${r.code ?? "unknown"}): ${r.message ?? ""}`);
      } else {
        setHasOverride(false);
        setStatus("Reverted to the code default. Reloading the default…");
        const seed = await api.get<SeedResponse>(`/api/email-templates/${kind}/seed`);
        setHtml(seed.html_body);
        setText(seed.text_body);
        setSample(seed.sample);
        setRules({ marketing: seed.marketing, text_required: seed.text_required });
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }, [kind]);

  const draftPreview = useMemo(
    () => fillSampleTokens(html, sample),
    [html, sample],
  );

  return (
    <EmailTemplateEditView
      kind={kind}
      loading={loading}
      loadError={loadError}
      hasOverride={hasOverride}
      marketing={rules.marketing}
      textRequired={rules.text_required}
      html={html}
      text={text}
      saving={saving}
      error={error}
      status={status}
      onHtmlChange={setHtml}
      onTextChange={setText}
      onSave={() => void onSave()}
      onReset={() => void onReset()}
      onPreview={() => setShowPreview(true)}
      onRetry={() => setLoadAttempt((n) => n + 1)}
      onBack={() => navigate("/email-templates")}
    >
      {showPreview ? (
        <EmailPreviewModal
          title={`Preview · ${kind}`}
          subtitle={draftPreviewSubtitle(
            draftPreview.unfilled,
            draftSaveProblems(rules, { html, text }),
          )}
          html={draftPreview.html}
          onClose={() => setShowPreview(false)}
        />
      ) : null}
    </EmailTemplateEditView>
  );
}
